import { useMemo, useRef, useState } from 'react'
import { parseMenuCsv, parseMenuRows, type MenuCsvError, type MenuRow } from '../../services/menuCsv'
import { saveCategory, saveProduct } from '../../services/products'
import type { Category, Product } from '../../types'
import { DownloadIcon } from '../icons'
import { useAuth } from '../../context/AuthContext'

interface Props {
  tenantId: string
  categories: Category[]
  products: Product[]
  onDone: () => void
  onCancel: () => void
}

const fold = (s: string) => s.toLocaleLowerCase('tr-TR')

/**
 * Örnek dosya (kullanıcı, 11 Eki 2026: CSV kafa karıştırıyordu, Excel
 * olsun). Excel dosyası olarak iniyor; geri yüklenen Excel arka planda
 * okunuyor. Eski CSV dosyaları da okunmaya devam ediyor.
 */
const SAMPLE: (string | number)[][] = [
  ['kategori', 'ürün', 'fiyat', 'açıklama'],
  ['Izgara', 'Adana Kebap', 300, 'Yanında pilav'],
  ['Izgara', 'Tavuk Şiş', 280, ''],
  ['Tatlı', 'Künefe', 180, 'Antep fıstıklı'],
]

/**
 * Tartılı ürünler modülü açıksa örnekte iki sütun daha: birim (adet / kg) ve
 * kap ("Porselen tabak 280": adı ve gramı). kg satırında fiyat 1 kg'ın.
 */
const SAMPLE_WEIGH: (string | number)[][] = [
  ['kategori', 'ürün', 'fiyat', 'açıklama', 'birim', 'kap'],
  ['Börek', 'Kıymalı Kol Böreği', 400, 'Fiyat 1 kg', 'kg', 'Porselen tabak 280'],
  ['Börek', 'Peynirli Su Böreği', 380, '', 'kg', 'Paket karton 120'],
  ['Tatlı', 'Fıstıklı Baklava', 900, 'Fiyat 1 kg', 'kg', ''],
  ['İçecek', 'Çay', 30, '', 'adet', ''],
  ['İçecek', 'Ayran', 40, '', '', ''],
]

const isExcel = (f: File) => /\.xlsx$/i.test(f.name) || f.type.includes('spreadsheetml')

type Stage =
  | { step: 'pick' }
  | { step: 'preview'; rows: MenuRow[]; errors: MenuCsvError[] }
  | { step: 'saving' }
  | { step: 'done'; created: number; updated: number; newCategories: number }
  /**
   * A write failed part-way. What already landed is named rather than hidden:
   * the import is not a transaction, rows are written one at a time, so
   * "kaydedilemedi" alone would leave someone guessing whether to run it
   * again and risk doubling whatever did get through. Re-running is in fact
   * safe (products are matched by name and updated, not duplicated), and the
   * message says so.
   */
  | { step: 'error'; message: string; created: number; updated: number; newCategories: number }

/**
 * Bringing a menu in from a spreadsheet rather than typing every item by
 * hand, the same category and product rows the menu screen already saves
 * one at a time, just many at once.
 *
 * Three stages, never skipped: pick a file, see exactly what will happen to
 * it, then save. The preview exists because an import is not reversible the
 * way one wrong product is, fifty rows land in one action, so the one
 * chance to catch a misread price or a typo'd category is before Kaydet, not
 * after it.
 */
export function MenuImportDialog({ tenantId, categories, products, onDone, onCancel }: Props) {
  const weighOn = useAuth().hasFeature('tarti')
  const [stage, setStage] = useState<Stage>({ step: 'pick' })
  const [fileError, setFileError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  // Existing categories, matched case- and Turkish-alphabet-insensitively,
  // "izgara" in the sheet must land on "Izgara" already on the menu rather
  // than opening a near-duplicate beside it.
  const byFoldedName = useMemo(() => {
    const map = new Map<string, Category>()
    for (const c of categories) map.set(fold(c.name), c)
    return map
  }, [categories])

  async function onFile(file: File) {
    setFileError(null)
    let parsed: ReturnType<typeof parseMenuCsv>
    try {
      if (isExcel(file)) {
        // Yalnız gerekince yükleniyor: kasa açılışını ağırlaştırmasın.
        const { default: readXlsxFile } = await import('read-excel-file')
        const sheet = await readXlsxFile(file)
        parsed = parseMenuRows(sheet.map((row) => row.map((c) => (c === null || c === undefined ? '' : String(c)))))
      } else if (/\.xls$/i.test(file.name)) {
        setFileError('Eski Excel biçimi (.xls) okunamıyor. Excel\'de "Farklı kaydet" ile .xlsx olarak kaydedin.')
        return
      } else {
        parsed = parseMenuCsv(await file.text())
      }
    } catch {
      setFileError('Dosya okunamadı. Örnek dosyayı indirip onun üzerine yazın.')
      return
    }
    // Modül kapalıyken kg satırı kaydedilmiyor: tartı ekranı olmadan satılamaz.
    const rows = weighOn ? parsed.rows : parsed.rows.filter((r) => r.byWeight !== true)
    const errors = weighOn
      ? parsed.errors
      : [
          ...parsed.errors,
          ...parsed.rows
            .filter((r) => r.byWeight === true)
            .map((r) => ({ row: 0, message: `${r.name}: kiloyla satış için Tartılı ürünler modülü açık değil.` })),
        ]
    if (rows.length === 0 && errors.length === 0) {
      setFileError('Dosyada okunacak bir satır bulunamadı.')
      return
    }
    setStage({ step: 'preview', rows, errors })
  }

  async function downloadSample() {
    const { default: writeXlsxFile } = await import('write-excel-file')
    const data = (weighOn ? SAMPLE_WEIGH : SAMPLE).map((row, i) =>
      row.map((v) =>
        i === 0
          ? { value: String(v), fontWeight: 'bold' as const, backgroundColor: '#FDE7DC' }
          : typeof v === 'number'
            ? { value: v, type: Number }
            : { value: v, type: String },
      ),
    )
    await writeXlsxFile(data, {
      fileName: 'menu-ornek.xlsx',
      sheet: 'Menü',
      columns: [{ width: 16 }, { width: 24 }, { width: 10 }, { width: 24 }, { width: 8 }, { width: 22 }].slice(
        0,
        data[0].length,
      ),
    })
  }

  async function onConfirm(rows: MenuRow[]) {
    setStage({ step: 'saving' })

    // Counted outside the try so a failure can still say how far it got.
    let created = 0
    let updated = 0
    let newCategories = 0

    try {

    // Categories are resolved first and in file order, so several rows for
    // a category that does not exist yet share the one row that creates it
    // rather than each racing to create their own. sortOrder continues from
    // whatever the menu already has, not from zero, importing into an
    // existing menu must add to the end of it, not interleave at the start.
    const catByFolded = new Map(byFoldedName)
    let nextCatOrder = 10 + Math.max(0, ...categories.map((c) => c.sortOrder))

    for (const row of rows) {
      const key = fold(row.category)
      if (catByFolded.has(key)) continue
      nextCatOrder += 10
      const id = await saveCategory(tenantId, {
        name: row.category,
        sortOrder: nextCatOrder,
        active: true,
      })
      catByFolded.set(key, { id, name: row.category, sortOrder: nextCatOrder, active: true })
      newCategories++
    }

    // A product already on the menu, matched by name within its resolved
    // category, is updated (price, description) rather than duplicated,
    // re-importing the same sheet after fixing a price corrects it instead
    // of doubling every row. sortOrder likewise continues per category
    // rather than resetting, so new rows land after what is already there.
    const existingByCategory = new Map<string, Map<string, Product>>()
    for (const p of products) {
      let byName = existingByCategory.get(p.categoryId)
      if (!byName) {
        byName = new Map()
        existingByCategory.set(p.categoryId, byName)
      }
      byName.set(fold(p.name), p)
    }
    const nextOrderByCategory = new Map<string, number>()
    for (const p of products) {
      nextOrderByCategory.set(
        p.categoryId,
        Math.max(nextOrderByCategory.get(p.categoryId) ?? 0, p.sortOrder),
      )
    }

    for (const row of rows) {
      const category = catByFolded.get(fold(row.category))!
      const existing = existingByCategory.get(category.id)?.get(fold(row.name))

      if (existing) {
        await saveProduct(tenantId, {
          id: existing.id,
          name: row.name,
          categoryId: category.id,
          priceKurus: row.priceKurus,
          description: row.description || null,
          active: existing.active,
          soldOut: existing.soldOut,
          sortOrder: existing.sortOrder,
          noteOptions: existing.noteOptions,
          // Birim boşsa var olan ayar kalıyor: eski 4 sütunlu dosya tartılı
          // ürünü adetliye çevirmesin. Kap da yazılmadıysa eskisi.
          byWeight: row.byWeight ?? existing.byWeight,
          tare: row.tare ?? ((row.byWeight ?? existing.byWeight) ? existing.tare : null),
        })
        updated++
      } else {
        const order = (nextOrderByCategory.get(category.id) ?? 0) + 10
        nextOrderByCategory.set(category.id, order)
        await saveProduct(tenantId, {
          name: row.name,
          categoryId: category.id,
          priceKurus: row.priceKurus,
          description: row.description || null,
          active: true,
          sortOrder: order,
          byWeight: row.byWeight === true,
          tare: row.tare,
        })
        created++
      }
    }

      setStage({ step: 'done', created, updated, newCategories })
    } catch (err) {
      // Left in 'saving' before this existed: a failed write threw, the stage
      // never moved, and the dialog sat on "Kaydediliyor…" with no way out.
      setStage({
        step: 'error',
        message: err instanceof Error ? err.message : 'Bilinmeyen hata',
        created,
        updated,
        newCategories,
      })
    }
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card dlg__card--wide">
        <h2>Menüyü Excel'den içe aktar</h2>

        {stage.step === 'pick' && (
          <>
            <p className="dlg__empty">
              Örnek Excel dosyasını indirin, ürünlerinizi kategori, ürün, fiyat ve
              açıklama sütunlarına yazıp kaydedin, sonra buradan seçin. Aynı
              kategori adı birden çok satırda geçebilir, hepsi aynı kategoriye
              eklenir.
            </p>
            {weighOn && (
              <p className="dlg__empty">
                Kiloyla satılan ürün için 5. sütuna <b>kg</b> yazın (fiyat 1 kg'ın),
                isterseniz 6. sütuna kabını adı ve gramıyla: <b>Porselen tabak 280</b>.
              </p>
            )}
            {/* Pencerenin düğmelerinden ayrı, tam genişlikte bir satır: alttaki
                Vazgeç / CSV dosyası seç'e yapışık yarım bir düğme gibi
                durmasın. */}
            <button type="button" className="csv__sample" onClick={() => void downloadSample()}>
              <DownloadIcon />
              <i className="btn__rule" />
              <span>
                <b>Örnek dosyayı indir</b>
                <small>Sütunları hazır bir Excel dosyası; doldurup geri yükleyin</small>
              </span>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void onFile(file)
                e.target.value = ''
              }}
            />
            <div className="dlg__buttons dlg__buttons--even">
              <button type="button" className="btn btn--ghost" onClick={onCancel}>
                Vazgeç
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => fileInput.current?.click()}
              >
                Excel dosyası seç
              </button>
            </div>
            {fileError && <p className="dlg__warn">{fileError}</p>}
          </>
        )}

        {stage.step === 'preview' && (
          <MenuImportPreview
            rows={stage.rows}
            errors={stage.errors}
            byFoldedName={byFoldedName}
            onBack={() => setStage({ step: 'pick' })}
            onConfirm={() => void onConfirm(stage.rows)}
          />
        )}

        {stage.step === 'saving' && <p className="dlg__empty">Kaydediliyor…</p>}

        {stage.step === 'error' && (
          <>
            <p className="dlg__empty">
              Kaydedilemedi: {stage.message}
              {(stage.newCategories > 0 || stage.created > 0 || stage.updated > 0) && (
                <>
                  {' '}Bundan önce
                  {stage.newCategories > 0 && ` ${stage.newCategories} kategori`}
                  {stage.created > 0 && ` ${stage.created} ürün eklendi`}
                  {stage.updated > 0 && ` ${stage.updated} ürün güncellendi`}
                  . Aynı dosyayı tekrar denemek güvenli: ürünler adlarına göre
                  eşleşip güncelleniyor, ikinci kez eklenmiyor.
                </>
              )}
            </p>
            <div className="dlg__buttons dlg__buttons--even">
              <button type="button" className="btn" onClick={() => setStage({ step: 'pick' })}>
                Tekrar dene
              </button>
              <button type="button" className="btn btn--primary" onClick={onDone}>
                Kapat
              </button>
            </div>
          </>
        )}

        {stage.step === 'done' && (
          <>
            <p className="dlg__empty">
              {stage.newCategories > 0 &&
                `${stage.newCategories} yeni kategori açıldı. `}
              {stage.created > 0 && `${stage.created} ürün eklendi. `}
              {stage.updated > 0 && `${stage.updated} ürün güncellendi.`}
            </p>
            <div className="dlg__buttons dlg__buttons--even">
              <button type="button" className="btn btn--primary" onClick={onDone}>
                Tamam
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function MenuImportPreview({
  rows,
  errors,
  byFoldedName,
  onBack,
  onConfirm,
}: {
  rows: MenuRow[]
  errors: MenuCsvError[]
  byFoldedName: Map<string, Category>
  onBack: () => void
  onConfirm: () => void
}) {
  const newCategoryNames = useMemo(() => {
    const seen = new Set<string>()
    const names: string[] = []
    for (const r of rows) {
      const key = fold(r.category)
      if (!byFoldedName.has(key) && !seen.has(key)) {
        seen.add(key)
        names.push(r.category)
      }
    }
    return names
  }, [rows, byFoldedName])

  return (
    <>
      <p className="dlg__empty">
        {rows.length} ürün okundu
        {newCategoryNames.length > 0 &&
          `, bunlardan ${newCategoryNames.length} yeni kategori altında (${newCategoryNames.join(', ')})`}
        .
      </p>

      {errors.length > 0 && (
        <div className="dlg__hint">
          {errors.length} satır atlanacak:
          <ul>
            {errors.slice(0, 6).map((e, i) => (
              <li key={i}>
                {e.row > 0 && `Satır ${e.row}: `}
                {e.message}
              </li>
            ))}
            {errors.length > 6 && <li>…ve {errors.length - 6} satır daha.</li>}
          </ul>
        </div>
      )}

      {rows.length > 0 && (
        <div className="menu-import__preview">
          <table>
            <thead>
              <tr>
                <th>Kategori</th>
                <th>Ürün</th>
                <th>Fiyat</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 8).map((r, i) => (
                <tr key={i}>
                  <td>{r.category}</td>
                  <td>{r.name}</td>
                  <td>
                    {(r.priceKurus / 100).toFixed(2)} ₺{r.byWeight && ' / kg'}
                    {r.tare && (
                      <small className="menu-import__tare">
                        {r.tare.name} {r.tare.gr} g
                      </small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 8 && (
            <p className="menu-import__more">…ve {rows.length - 8} satır daha.</p>
          )}
        </div>
      )}

      <div className="dlg__buttons dlg__buttons--even">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          Geri
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={rows.length === 0}
          onClick={onConfirm}
        >
          {rows.length} ürünü içe aktar
        </button>
      </div>
    </>
  )
}
